import React, { useState } from "react";
import { FiX, FiCheck, FiTrash2 } from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";

/**
 * OpeningBalanceModal — add / edit / delete a BUSINESS opening balance.
 *
 * Used by BOTH the business profile card and the Ledger page (customer tab),
 * so the rules are identical everywhere. All validation is repeated on the
 * server (PATCH / DELETE /api/business/:id/opening-balance).
 *
 * Props
 *   business  – business object (needs id, companyName, opening_* fields)
 *   onClose   – close the popup
 *   onSaved   – called with the updated business after save / delete
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 };
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px",
  border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none",
  fontFamily: "var(--font-body)",
};

const TYPES = [
  { key: "debit",  title: "They owe us",        hint: "Customer had an unpaid balance (receivable) before the system started." },
  { key: "credit", title: "Advance / we owe them", hint: "Customer had paid in advance (credit) before the system started." },
];

const OpeningBalanceModal = ({ business, onClose, onSaved }) => {
  const existing = Number(business?.opening_balance || 0) > 0;

  const [form, setForm] = useState({
    opening_balance:      existing ? String(Number(business.opening_balance)) : "",
    opening_balance_type: business?.opening_balance_type || "debit",
    opening_balance_date: business?.opening_balance_date ? String(business.opening_balance_date).slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const amount = Number(form.opening_balance);
  const busy = saving || deleting;

  const handleSave = async () => {
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter an amount greater than 0.");
    if (!form.opening_balance_date) return toast.error("Select the opening balance date.");

    try {
      setSaving(true);
      const { data } = await API.patch(`/business/${business.id}/opening-balance`, {
        opening_balance:      amount,
        opening_balance_type: form.opening_balance_type,
        opening_balance_date: form.opening_balance_date,
      });
      toast.success(existing ? "Opening balance updated" : "Opening balance added");
      if (data.warning) toast(data.warning, { icon: "⚠️", duration: 8000 });
      onSaved && onSaved(data.business);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save opening balance");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the opening balance of ${money(business.opening_balance)} for ${business.companyName}?`)) return;
    try {
      setDeleting(true);
      const { data } = await API.delete(`/business/${business.id}/opening-balance`);
      toast.success("Opening balance deleted");
      onSaved && onSaved(data.business);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete opening balance");
    } finally {
      setDeleting(false);
    }
  };

  const activeType = TYPES.find((t) => t.key === form.opening_balance_type) || TYPES[0];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 28, width: 440, maxWidth: "95vw", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              {existing ? "Edit Opening Balance" : "Add Opening Balance"}
            </h3>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{business?.companyName}</div>
          </div>
          <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Amount (₹)</label>
            <input
              type="number" step="0.01" min="0" style={inputStyle}
              value={form.opening_balance}
              onChange={(e) => set("opening_balance", e.target.value)}
              placeholder="0.00" autoFocus
            />
          </div>

          <div>
            <label style={labelStyle}>Type</label>
            <div style={{ display: "flex", gap: 8 }}>
              {TYPES.map((t) => {
                const on = form.opening_balance_type === t.key;
                return (
                  <button
                    key={t.key} type="button" onClick={() => set("opening_balance_type", t.key)}
                    style={{
                      flex: 1, padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
                      fontFamily: "var(--font-body)",
                      border: on ? "2px solid #2563eb" : "1px solid #e5e7eb",
                      background: on ? "#eff6ff" : "#fff", color: on ? "#2563eb" : "#6b7280",
                    }}
                  >
                    {t.title}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{activeType.hint}</div>
          </div>

          <div>
            <label style={labelStyle}>As-on Date</label>
            <input
              type="date" style={inputStyle}
              value={form.opening_balance_date}
              onChange={(e) => set("opening_balance_date", e.target.value)}
            />
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
              The ledger starts from this date. Bills and receipts dated before it are not shown.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 22, alignItems: "center" }}>
          {existing && (
            <button
              onClick={handleDelete} disabled={busy}
              style={{ padding: "8px 14px", border: "1px solid #fecaca", borderRadius: 8, cursor: busy ? "not-allowed" : "pointer", background: "#fff", color: "#dc2626", fontSize: 14, fontFamily: "var(--font-body)", display: "flex", alignItems: "center", gap: 6 }}
            >
              <FiTrash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={busy} style={{ padding: "8px 18px", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", fontFamily: "var(--font-body)", background: "#fff", fontSize: 14 }}>
            Cancel
          </button>
          <button
            onClick={handleSave} disabled={busy}
            style={{ padding: "8px 20px", border: "none", borderRadius: 8, cursor: busy ? "not-allowed" : "pointer", fontFamily: "var(--font-body)", background: "#2563eb", color: "#fff", fontWeight: 600, fontSize: 14, opacity: busy ? 0.7 : 1, display: "flex", alignItems: "center", gap: 6 }}
          >
            <FiCheck size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpeningBalanceModal;
